/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useEffect } from 'react';
import { IoCloseOutline, IoCalendarClearOutline } from 'react-icons/io5';
import PropTypes from 'prop-types';

import './datepicker.css';
import { global } from '../../../../shared/colors';

const Datepicker = ({ selected, onChange }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(selected);
  const [currentDate, setCurrentDate] = useState(new Date());

  const handleDayClick = (dayString) => {
    setSelectedDate(dayString);
    if (onChange) {
      onChange(dayString);
    }
  };

  const handleSave = () => {
    setShowCalendar(false);
  };

  const handleClear = () => {
    setSelectedDate(null);
    setShowCalendar(false);
    if (onChange) {
      onChange(null);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setShowCalendar(false);
    if (onChange) {
      onChange(null);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const handlePreviousYear = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1)
    );
  };

  const handleNextYear = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1)
    );
  };

  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const generateWeekdays = () => weekdays.map((day) => (
    <div key={day} className="weekday">
      {day}
    </div>
  ));

  const generateCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1).getDay();

    for (let i = 0; i < firstDayOfMonth; i += 1) {
      days.push(
        <div key={`empty-${i}`} className="day-button empty">
          {' '}
        </div>
      );
    }

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();

    for (let i = 1; i <= daysInMonth; i += 1) {
      const dayDate = new Date(year, month, i);
      const dayString = dayDate.toISOString().split('T')[0];
      const formattedDayNumber = i.toString().padStart(2, '0');

      const isToday = today.getDate() === dayDate.getDate()
                && today.getMonth() === dayDate.getMonth()
                && today.getFullYear() === dayDate.getFullYear();

      let buttonClass = 'day-button';
      if (selectedDate === dayString) {
        buttonClass = 'day-button active';
      } else if (isToday) {
        buttonClass = 'day-button today';
      }

      days.push(
        <button
          type="button"
          key={i}
          className={buttonClass}
          onClick={() => handleDayClick(dayString)}
        >
          {formattedDayNumber}
        </button>
      );
    }
    return days;
  };

  const formatDate = (date) => {
    const options = {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    };
    return date.toLocaleDateString(undefined, options).replace(' ', ', ');
  };

  const formatDateForSubHeader = (dateString) => {
    const date = new Date(dateString);

    const options = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    };

    return date.toLocaleDateString(undefined, options);
  };

  const formatDateForHeader = (dateString) => {
    const date = new Date(dateString);

    const options = {
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    };

    return date.toLocaleDateString(undefined, options);
  };

  const HeaderDate = () => (
    <>
      <h1 className="headerDate">{formatDateForHeader(selectedDate)}</h1>
      <h2 className="headerSubDate">
        {formatDateForSubHeader(selectedDate)}
      </h2>
    </>
  );

  const TodayDate = () => {
    const todayString = selectedDate || new Date().toISOString().split('T')[0];

    return (
      <>
        <h1 className="headerDate">Today</h1>
        <h2 className="headerSubDate">
          {formatDateForSubHeader(todayString)}
        </h2>
      </>
    );
  };

  const headerTitle = selectedDate ? <HeaderDate /> : <TodayDate />;

  const currentMonthYear = formatDate(currentDate);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const formattedDay = date.getDate().toString().padStart(2, '0');
    const formattedMonth = (date.getMonth() + 1)
      .toString()
      .padStart(2, '0');
    const formattedYear = date.getFullYear();
    return `${formattedMonth} / ${formattedDay} / ${formattedYear}`;
  };

  useEffect(() => {
    setSelectedDate(selected || '');
  }, [selected]);

  return (
    <div className="datepicker-container">
      <div className="input-wrapper">
        <input
          type="text"
          value={formatDateForInput(selectedDate || selected)}
          readOnly
          onClick={() => setShowCalendar(true)}
          placeholder="Select date"
        />
        <IoCalendarClearOutline
          className="calendar-icon"
          onClick={() => setShowCalendar(true)}
          style={{
            color: global.Gray3,
            fontSize: '24px',
            marginRight: '20px',
            stroke: '1px'
          }}
        />
      </div>

      {showCalendar && (
        <div className="calendar-panel">
          <div className="calendar-header">
            <span>{headerTitle}</span>
            <button
              type="button"
              className="close-button"
              onClick={handleClose}
            >
              <IoCloseOutline size={40} />
            </button>
          </div>
          <section className="month-section">
            <div className="month-year-header">
              <div>
                <button
                  type="button"
                  className="arrow-button"
                  onClick={handlePreviousYear}
                >
                  &laquo;
                </button>
                <button
                  type="button"
                  className="arrow-button"
                  onClick={handlePreviousMonth}
                >
                  &lsaquo;
                </button>
              </div>
              <span className="currentMonthYear">
                {currentMonthYear}
              </span>
              <div>
                <button
                  type="button"
                  className="arrow-button"
                  onClick={handleNextMonth}
                >
                  &rsaquo;
                </button>
                <button
                  type="button"
                  className="arrow-button"
                  onClick={handleNextYear}
                >
                  &raquo;
                </button>
              </div>
            </div>
            <div className="weekdays-grid">
              {generateWeekdays()}
            </div>
            <div className="days-grid">
              {generateCalendarDays()}
            </div>
          </section>
          <div className="panel-actions">
            <button
              className="cancel"
              type="button"
              onClick={handleClear}
            >
              Cancel
            </button>
            <button
              className="save"
              type="button"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

Datepicker.propTypes = {
  selected: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

export default Datepicker;
